import React, { useCallback, useState, useEffect } from "react";
import FaiBox from "../../../../../../MuiComponents/FaiBox";
import FaiTypography from "../../../../../../MuiComponents/FaiTypography";
import theme from "../../../../../../assets/theme";
import {
	Grid,
	CircularProgress,
	Icon,
	Button,
	IconButton,
} from "@mui/material";
import FaiDropzone from "../../../../../../MuiComponents/FaiDropzone";
import { ProductController } from "../../../../../../controllers/productController";
import FormField from "../../../../../../components/FormField";
import FaiSelect from "../../../../../../MuiComponents/FaiSelect";
import FaiInput from "../../../../../../MuiComponents/FaiInput";
import Dropzone from "dropzone";
import { Product, ProductVariant } from "../../../../../../types/productTypes";
import DeleteIcon from "@mui/icons-material/Delete";
import { DropzoneArea } from "react-mui-dropzone";
import { toast } from "react-toastify";

interface VariantFormProps {
	formData: Partial<Product>;
	setFormData: React.Dispatch<React.SetStateAction<Partial<Product>>>;
	variantId?: string;
}

interface Variant {
	id: string;
	externalProductId: string;
	externalProductIdType: string;
	skuCode: string;
	name: string;
	quantity: number;
	price: number;
	color: string;
	size: string[];
	media: File[];
	mediaPreview?: string;
}

function VariantForm({ formData, setFormData, variantId }: VariantFormProps) {
	const { typography, palette } = theme;
	const [variants, setVariants] = useState<Variant[]>([
		{
			id: "1",
			externalProductId: "",
			externalProductIdType: "",
			skuCode: "",
			name: "",
			quantity: 0,
			price: 0,
			color: "",
			size: [],
			media: [],
			mediaPreview: null,
		},
	]);
	const [uploading, setUploading] = useState(false);
	const [uploadSuccess, setUploadSuccess] = useState(false);
	const [uploadError, setUploadError] = useState("");

	const [variantImages, setVariantImages] = useState<{ [key: string]: File[] }>(
		{}
	);
	const [mediaVariants, setMediaVariants] = useState<any>();

	// Initialize variants from formData when editing
	useEffect(() => {
			const initializedVariants = formData.variants?.map((variant, index) => {
				// Extract color and size from variantAttributes
				const colorAttr = variant.variantAttributes?.find(
					(attr) => attr.attribute === "Color"
				);
				const sizeAttrs = variant.variantAttributes?.filter(
					(attr) => attr.attribute === "Size"
				);

				// Convert media to File[] only
				let mediaFiles = [];
				if (variant.media && Array.isArray(variant.media)) {
					// Check if the first item is a File to determine the type
					if (variant.media.length > 0 && variant.media[0]) {
						mediaFiles = variant.media;
					}
				}

				return {
					id: variant.id || String(index + 1),
					externalProductId: variant.externalProductId || "",
					externalProductIdType: variant.externalProductIdType || "",
					skuCode: variant.skuCode || "",
					name: variant.name || "",
					quantity: variant?.inventory?.quantity || variant?.inventoryQuantity || 0,
					price: variant?.pricing?.sellingPrice || variant?.price || 0,
					color: colorAttr?.value || "",
					size: sizeAttrs?.map((attr) => attr.value) || [],
					media: mediaFiles,
					mediaPreview: null,
				};
			});
			setVariants(initializedVariants);
			setMediaVariants((prev) => (prev ? prev : initializedVariants));

			// Sync IDs back to formData so that handleVariantChange can match by ID
			if (initializedVariants && initializedVariants.length > 0) {
				setFormData((prev) => ({
					...prev,
					variants: prev.variants?.map((variant, index) => ({
						...variant,
						id: variant.id || String(index + 1),
					})) || [],
				}));
			}
	}, []);

	// Cleanup dropzone instances on unmount
	useEffect(() => {
		return () => {
			// Cleanup any existing dropzone instances
			const dropzones = document.querySelectorAll(".dz-preview");
			dropzones.forEach((dropzone) => {
				if (dropzone.parentNode) {
					dropzone.parentNode.removeChild(dropzone);
				}
			});
		};
	}, []);
	const [categoryImages, setCategoryImages] = useState<
		(File | string | null)[]
	>([]);

	const handleAddVariant = () => {
		// Unique id shared by both the local row and the formData row. Using a
		// length-based id ("new-2") collided after a remove+add (two rows ended up
		// with the same id), so editing one variant silently changed another.
		const newId = "new-" + Date.now();
		const newVariant = {
			id: newId,
			externalProductId: "",
			externalProductIdType: "",
			skuCode: "",
			name: "",
			quantity: 0,
			price: 0,
			color: "",
			size: [],
			media: [],
			mediaPreview: null,
		};
		setVariants([...(variants || []), newVariant]);

		// Update parent formData
		setFormData((prev) => ({
			...prev,
			variants: [
				...(prev.variants || []),
				{
					id: newId,
					externalProductId: "",
					externalProductIdType: "",
					relationshipType: "variant",
					skuCode: "",
					name: "",
					variantAttributes: [],
					pricing: { mrp: 0, sellingPrice: 0 },
					inventory: { quantity: 0 },
					media: [] as File[], // Add media field to formData variant with proper typing
				},
			],
		}));
	};

	const handleRemoveVariant = (variantId: string) => {
		setVariants((prev) => (prev || []).filter((variant, index) => (variant.id || index+1) !== variantId));

		// Clean up variantImages state
		setVariantImages((prev) => {
			const newVariantImages = { ...prev };
			delete newVariantImages[variantId];
			return newVariantImages;
		});

		// Update parent formData
		setFormData((prev) => {
			// Collect removed variant IDs
			const removedIds = [ ...(prev.variantIdsToBeRemoved || []) ];
			removedIds.push(variantId);
			return {
				...prev,
				variants:
					prev.variants?.filter((_, index) => ((_.id || index +1) !== variantId)) || [],
				variantIdsToBeRemoved: removedIds,
			};
		});
	};

	const handleVariantChange = (
		variantId: string,
		field: keyof Variant,
		value: any
	) => {
		// Find the index of the variant in local state for fallback matching
		const variantIndex = (variants || []).findIndex((v) => v.id === variantId);

		setVariants((prev) =>
			(prev || []).map((variant) =>
				variant.id === variantId ? { ...variant, [field]: value } : variant
			)
		);

		// Update parent formData
		setFormData((prev) => {
			const updatedVariants =
				prev.variants?.map((variant, index) => {
					// Match by id if available, otherwise match by index (for new variants without id)
					if (variant.id === variantId || (variant.id === undefined && index === variantIndex)) {
						const updatedVariant = { ...variant };

						switch (field) {
							case "externalProductId":
								updatedVariant.externalProductId = value;
								break;
							case "externalProductIdType":
								updatedVariant.externalProductIdType = value;
								break;
							case "skuCode":
								updatedVariant.skuCode = value;
								break;
							case "name":
								updatedVariant.name = value;
								break;
							case "quantity":
								updatedVariant.inventory = {
									...updatedVariant.inventory,
									quantity: value,
								};
								break;
							case "price":
								updatedVariant.price = value;
								updatedVariant.pricing = {
									...updatedVariant.pricing,
									sellingPrice: value,
								};
								break;
							case "color":
								updatedVariant.variantAttributes = [
									...(updatedVariant.variantAttributes || []).filter(
										(attr) => attr.attribute !== "Color"
									),
									{ attribute: "Color", value },
								];
								break;
							case "size":
								updatedVariant.variantAttributes = [
									...(updatedVariant.variantAttributes || []).filter(
										(attr) => attr.attribute !== "Size"
									),
									...value.map((size: string) => ({
										attribute: "Size",
										value: size,
									})),
								];
								break;
							case "media":
								updatedVariant.media = value;
								break;
						}
						return updatedVariant;
					}
					return variant;
				}) || [];

			return {
				...prev,
				variants: updatedVariants,
			};
		});
	};

	const handleImageChange = (index: number, files: File[]) => {
		// Update the local state for UI display
		// const newImages = [...categoryImages];
		// newImages[index] = files.length > 0 ? files[0] : null;
		setCategoryImages(files);

		// Update the variant's media in formData
		setFormData((prev) => {
			const updatedVariants =
				prev.variants?.map((variant, variantIndex) => {
					if (variantIndex === index) {
						return {
							...variant,
							media: files, // Add the files to the variant's media array
						};
					}
					return variant;
				}) || [];

			return {
				...prev,
				variants: updatedVariants,
			};
		});
	};

	const handleDeleteVariantMedia = async (variantId, mediaKey) => {
		if (!variantId || !mediaKey) {
			toast.error("Missing variant or media key.");
			return;
		}
		try {
			await ProductController.deleteVariantMedia(variantId, mediaKey);
			// Remove the image from the UI
			setVariants((prevVariants) =>
				(prevVariants || []).map((variant) =>
					variant.id === variantId
						? {
								...variant,
								media: Array.isArray(variant.media)
									? variant.media.filter((img: any) => {
											return !img?.includes(mediaKey);
									  })
									: variant.media,
						  }
						: variant
				)
			);

			// Also update mediaVariants
			setMediaVariants((prevMediaVariants) =>
				prevMediaVariants.map((variant) =>
					variant.id === variantId
						? {
								...variant,
								media: Array.isArray(variant.media)
									? variant.media.filter((img: any) => {
											return !img?.includes(mediaKey);
									  })
									: variant.media,
						  }
						: variant
				)
			);

			toast.success("Variant image deleted successfully.");
		} catch (error) {
			toast.error("Failed to delete variant image.");
		}
	};

	// Alpha apparel sizing, XS through 6XL. Kept as a closed list on purpose —
	// free text produces "Large" / "large" / "LG" variants that break filtering
	// and the storefront's size ordering.
	//
	// Values are lowercase because that is what already exists in Mongo for the
	// current catalogue; labels are what the merchant sees. "xxl" (not "2xl")
	// is retained for the same reason — renaming it would orphan every product
	// already saved with that value.
	const sizeOptions = [
		{ value: "xs", label: "XS" },
		{ value: "s", label: "S" },
		{ value: "m", label: "M" },
		{ value: "l", label: "L" },
		{ value: "xl", label: "XL" },
		{ value: "xxl", label: "XXL" },
		{ value: "3xl", label: "3XL" },
		{ value: "4xl", label: "4XL" },
		{ value: "5xl", label: "5XL" },
		{ value: "6xl", label: "6XL" },
	];

	return (
		<>
			<FaiBox className={"variants"} mb={4}>
				<FaiBox
					display="flex"
					justifyContent="space-between"
					alignItems="center"
					mb={2}
				>
					<FaiTypography
						variant={"h5"}
						fontSize={typography.size.lg}
						fontWeight={typography.fontWeightMedium}
					>
						Product Variants
					</FaiTypography>
					<Button
						variant="contained"
						color="primary"
						onClick={handleAddVariant}
					>
						Add Variant
					</Button>
				</FaiBox>

				{(variants || []).map((variant, index) => (
					<FaiBox
						key={variant.id}
						mb={4}
						p={2}
						sx={{ border: "1px solid #e0e0e0", borderRadius: 1 }}
					>
						<FaiBox
							display="flex"
							justifyContent="space-between"
							alignItems="center"
							mb={2}
						>
							<FaiTypography variant={"h6"}>Variant {index + 1}</FaiTypography>
							{variants.length > 1 && (
								<IconButton
									color="error"
									onClick={() => handleRemoveVariant(variant.id)}
									size="small"
								>
									<DeleteIcon />
								</IconButton>
							)}
						</FaiBox>

						<Grid container spacing={2}>
							<Grid item xs={12} sm={6}>
								<FormField
									label="External Product ID"
									name={`externalProductId-${variant.id}`}
									placeholder="Enter External Product ID"
									value={variant.externalProductId}
									onChange={(e) =>
										handleVariantChange(
											variant.id,
											"externalProductId",
											e.target.value
										)
									}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<FormField
									label="External Product ID Type"
									name={`externalProductIdType-${variant.id}`}
									placeholder="Enter External Product ID Type"
									value={variant.externalProductIdType}
									onChange={(e) =>
										handleVariantChange(
											variant.id,
											"externalProductIdType",
											e.target.value
										)
									}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<FormField
									label="SKU Code"
									name={`skuCode-${variant.id}`}
									placeholder="Enter SKU Code"
									value={variant.skuCode}
									onChange={(e) =>
										handleVariantChange(variant.id, "skuCode", e.target.value)
									}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<FormField
									label="Name"
									name={`name-${variant.id}`}
									placeholder="Enter Variant Name"
									value={variant.name}
									onChange={(e) =>
										handleVariantChange(variant.id, "name", e.target.value)
									}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<FormField
									label="Quantity"
									name={`quantity-${variant.id}`}
									placeholder="Enter Quantity"
									type="number"
									inputProps={{ min: 0 }}
									value={variant.quantity}
									onChange={(e) =>
										handleVariantChange(
											variant.id,
											"quantity",
											// Never allow a negative / NaN stock value through.
											Math.max(0, parseInt(e.target.value) || 0)
										)
									}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<FormField
									label="Price"
									name={`price-${variant.id}`}
									placeholder="Enter Price"
									type="number"
									value={variant.price}
									onChange={(e) =>
										handleVariantChange(
											variant.id,
											"price",
											parseFloat(e.target.value)
										)
									}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<FormField
									label="Color"
									name={`color-${variant.id}`}
									placeholder="Enter Color"
									value={variant.color}
									onChange={(e) =>
										handleVariantChange(variant.id, "color", e.target.value)
									}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<FaiBox mb={1}>
									<FaiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
										<FaiTypography
											component="label"
											variant="caption"
											fontWeight="bold"
											textTransform="capitalize"
										>
											Size
										</FaiTypography>
									</FaiBox>
									<FaiSelect
										name={`size-${variant.id}`}
										placeholder="Choose Sizes"
										isMulti
										value={variant.size.map((size) => ({
											value: size,
											label: size.toUpperCase(),
										}))}
										onChange={(
											selectedOptions: { value: string; label: string }[] | null
										) => {
											const selectedSizes = selectedOptions
												? selectedOptions.map((option) => option.value)
												: [];
											handleVariantChange(variant.id, "size", selectedSizes);
										}}
										options={sizeOptions}
									/>
								</FaiBox>
							</Grid>
							<Grid item xs={12}>
								<FaiBox mb={1}>
									<FaiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
										<FaiTypography
											component="label"
											variant="caption"
											fontWeight="bold"
											textTransform="capitalize"
										>
											Variant Images
										</FaiTypography>
									</FaiBox>
									<DropzoneArea
										onChange={(files) => handleImageChange(index, files)}
										dropzoneText="Drag your file(s) or browse"
										filesLimit={50}
									/>
									<FaiBox className="flex flex-wrap gap-4 my-4">
										{mediaVariants?.[index]?.media &&
											mediaVariants?.[index]?.media.map((image: any, idx) => {
												if (image) {
													return (
														<FaiBox
															alignItems="center"
															className="flex border p-2"
															key={image}
														>
															<img
																src={image}
																alt=""
																className="h-32 w-32 m-2"
															/>
															<FaiBox ml={1}>
																<DeleteIcon
																	fontSize="medium"
																	style={{ cursor: "pointer", color: "red" }}
																	onClick={() => {
																		const key = image.replace(
																			"https://s3.ap-south-1.amazonaws.com/fashion-ai-bucket/",
																			""
																		);
																		handleDeleteVariantMedia(variant.id, key);
																	}}
																/>
															</FaiBox>
														</FaiBox>
													);
												}
												return null;
											})}
									</FaiBox>
								</FaiBox>
							</Grid>
						</Grid>

						{uploading && (
							<FaiBox display="flex" alignItems="center" mt={2}>
								<CircularProgress size={20} />
								<FaiTypography variant="body2" ml={1}>
									Uploading...
								</FaiTypography>
							</FaiBox>
						)}

						{uploadError && (
							<FaiTypography variant="body2" color="error" mt={2}>
								{uploadError}
							</FaiTypography>
						)}

						{uploadSuccess && (
							<FaiTypography variant="body2" color="success" mt={2}>
								Images uploaded successfully!
							</FaiTypography>
						)}
					</FaiBox>
				))}
			</FaiBox>
		</>
	);
}

export default VariantForm;
